import { invoke } from '@tauri-apps/api/core';
import { useNotificationStore } from '../../store/notificationStore';
// Export re-auth that the HOST enforces: a single-use ticket per export.
import { authorizeVaultExport } from '../../utils/vaultExportAuth';
import { exportBitwardenCsv, exportLastpassCsv } from '../../utils/vaultPortability';
import { exportErrorMessage, saveTextFile } from './vaultImportExportHelpers';

/** Export: format, password checks and running the export. */
export function useVaultExport({
  t,
  onClose,
  masterRef,
  exportFormat,
  setExportFormat,
  exportPassword,
  exportPasswordConfirm,
  plainAck,
  setPlainAck,
  masterSecret,
  exporting,
  setExporting,
  setError,
}) {
  // Spread, not `.length`: the Rust floor is `password.chars().count() < 8`
  // (vault_export.rs), and `String.length` counts UTF-16 units. On astral
  // input the two disagree — `👍👍👍👍` is 8 units and 4 characters, so a
  // bare `.length` armed the button for a password the host then refused with
  // `export_password_too_short`. The unit has to be the one the trust
  // boundary uses.
  const exportPasswordOk =
    [...exportPassword].length >= 8 && exportPassword === exportPasswordConfirm;
  const exportPasswordMismatch =
    exportPasswordConfirm !== '' && exportPassword !== exportPasswordConfirm;
  // One source of truth for whether Export may run — the button's disabled
  // state AND the guard inside runExport, so neither Enter-in-a-field nor a
  // programmatic click can slip past what the UI shows.
  const canExport =
    !exporting &&
    masterSecret !== '' &&
    (exportFormat === 'encrypted' ? exportPasswordOk : plainAck);

  // The CSV formats are plaintext with a different serializer: same host
  // sourcing (vault_export_data plaintext), same acknowledgement, only the
  // final string differs. Declared here rather than branched in runExport so
  // adding a format is a row, not a new code path.
  const CSV_EXPORTS = {
    bitwardenCsv: {
      build: exportBitwardenCsv,
      filename: 'apiboo-vault-bitwarden.csv',
      labelKey: 'vaultIO.formatBitwardenCsv',
      fallback: 'Bitwarden CSV',
    },
    lastpassCsv: {
      build: exportLastpassCsv,
      filename: 'apiboo-vault-lastpass.csv',
      labelKey: 'vaultIO.formatLastpassCsv',
      fallback: 'LastPass CSV',
    },
  };

  // The acknowledgement is a per-decision tick, not a sticky preference —
  // cleared on EVERY format change, including plaintext-to-CSV, so each
  // plaintext-class choice is acknowledged on its own.
  const changeExportFormat = (next) => {
    setExportFormat(next);
    setPlainAck(false);
    setError(null);
  };

  const runExport = async () => {
    if (!canExport) return;
    setExporting(true);
    setError(null);
    try {
      // Re-auth precedes EVERYTHING, both formats, every time. This is the M2
      // gate: an unlocked vault is not consent to dump it.
      const ticket = await authorizeVaultExport(masterSecret);
      if (!ticket) {
        setError(t('vaultIO.reauthWrong', 'Wrong master passphrase. Nothing was exported.'));
        setExporting(false);
        masterRef.current?.select();
        return;
      }
      // Assembly happens host-side; the only thing JS ever holds is the final
      // file string — for "encrypted" that is ciphertext end to end. A CSV
      // export asks the host for the plaintext items (it is a plaintext
      // format, gated as one) and only serializes the answer.
      const csv = CSV_EXPORTS[exportFormat];
      const content = await invoke('vault_export_data', {
        format: csv ? 'plaintext' : exportFormat,
        password: exportFormat === 'encrypted' ? exportPassword : null,
        ticket,
      });
      let text = typeof content === 'string' ? content : JSON.stringify(content);
      let filename =
        exportFormat === 'encrypted' ? 'apiboo-vault-encrypted.json' : 'apiboo-vault.json';
      let ext = 'json';
      let mime = 'application/json';
      if (csv) {
        const parsed = JSON.parse(text);
        text = csv.build(Array.isArray(parsed?.items) ? parsed.items : []);
        filename = csv.filename;
        ext = 'csv';
        mime = 'text/csv';
      }
      const res = await saveTextFile(text, filename, ext, mime);
      if (res.cancelled) {
        setExporting(false);
        return;
      }
      useNotificationStore.getState().success(t('vaultIO.exportDone', 'Vault exported.'));
      onClose();
    } catch (e) {
      // Mapped to the sentence written for it; an unrecognised rejection (an
      // IPC failure, a file-write error) still shows its own text rather than
      // generic copy that would hide it.
      setError(exportErrorMessage(t, e));
      setExporting(false);
    }
  };

  const onExportKeyDown = (e) => {
    if (e.key === 'Enter') runExport();
  };

  return {
    exportPasswordMismatch,
    canExport,
    CSV_EXPORTS,
    changeExportFormat,
    runExport,
    onExportKeyDown,
  };
}
